/**
 * Played chapters tracking
 *
 * Tracks which chapters of a file have been listened to.
 *
 * The module is structured around a swappable `provider` so the storage
 * backend (currently browser localStorage) can later be replaced with an
 * API-backed implementation without touching call sites.
 *
 * To swap the backend later, build an object that implements the same
 * provider interface and pass it to `setPlayedChaptersProvider()`.
 */

const STORAGE_PREFIX = 'played-chapters-';

/**
 * @typedef {Object} PlayedChaptersProvider
 * @property {(fileId: string) => Promise<Set<string>>} getPlayed
 * @property {(fileId: string, chapterId: string) => Promise<void>} markPlayed
 * @property {(fileId: string, chapterId: string) => Promise<void>} markUnplayed
 * @property {(fileId: string) => Promise<Record<string, number>>} getPlayStates
 * @property {(fileId: string, chapterId: string, value: number) => Promise<void>} setPlayState
 * @property {() => Promise<void>} signOutCleanup
 */

/** @type {PlayedChaptersProvider} */
const localStorageProvider = {
  async getPlayed(fileId) {
    const key = STORAGE_PREFIX + fileId;
    try {
      const stored = localStorage.getItem(key);
      if (!stored) return new Set();
      const data = JSON.parse(stored);
      if (!Array.isArray(data)) return new Set();
      return new Set(data);
    } catch (e) {
      console.warn('Failed to read played chapters:', e);
      return new Set();
    }
  },

  async markPlayed(fileId, chapterId) {
    const key = STORAGE_PREFIX + fileId;
    try {
      const current = await localStorageProvider.getPlayed(fileId);
      current.add(chapterId);
      localStorage.setItem(key, JSON.stringify(Array.from(current)));
    } catch (e) {
      console.warn('Failed to mark chapter as played:', e);
    }
  },

  async markUnplayed(fileId, chapterId) {
    const key = STORAGE_PREFIX + fileId;
    try {
      const current = await localStorageProvider.getPlayed(fileId);
      current.delete(chapterId);
      localStorage.setItem(key, JSON.stringify(Array.from(current)));
    } catch (e) {
      console.warn('Failed to mark chapter as unplayed:', e);
    }
  },

  async getPlayStates(fileId) {
    const played = await localStorageProvider.getPlayed(fileId);
    const states = {};
    for (const id of played) states[id] = -1;
    return states;
  },

  async setPlayState(fileId, chapterId, value) {
    if (value === -1) {
      await localStorageProvider.markPlayed(fileId, chapterId);
    } else if (value === 0) {
      await localStorageProvider.markUnplayed(fileId, chapterId);
    }
    // Positive values (resume positions) are not supported by the legacy
    // provider — silently ignored to keep callers backend-agnostic.
  },

  async signOutCleanup() {
    // No-op: the legacy provider has no per-account namespace to flush.
  },
};

let activeProvider = localStorageProvider;

/**
 * Replace the active provider. Useful for swapping in an API-backed
 * implementation in the future.
 * @param {PlayedChaptersProvider} provider
 */
export function setPlayedChaptersProvider(provider) {
  activeProvider = provider;
}

/**
 * Restore the original localStorage-backed provider. Intended for tests
 * that swap in a custom provider via `setPlayedChaptersProvider` and need
 * to undo the swap before the next suite runs.
 */
export function _resetPlayedChaptersProvider() {
  activeProvider = localStorageProvider;
}

/**
 * Get the integer playback state per chapter for a file.
 *
 * Values:
 * - `0`  — not played
 * - `-1` — fully played
 * - `>0` — resume position in seconds
 *
 * @param {string} fileId
 * @returns {Promise<Record<string, number>>}
 */
export function getPlayStates(fileId) {
  return activeProvider.getPlayStates(fileId);
}

/**
 * Set the integer playback state for a chapter.
 * @param {string} fileId
 * @param {string} chapterId
 * @param {number} value `-1` (fully played), `0` (clear), or a positive integer (resume seconds)
 * @returns {Promise<void>}
 */
export function setPlayState(fileId, chapterId, value) {
  return activeProvider.setPlayState(fileId, chapterId, value);
}

/**
 * Flush every persisted artefact owned by the active provider on sign-out.
 * Safe to call when no user is signed in.
 * @returns {Promise<void>}
 */
export function signOutCleanup() {
  return activeProvider.signOutCleanup();
}

/**
 * Get the set of fully-played chapter IDs for a file. Derived from the
 * integer state map for backward compatibility with binary call sites.
 * @param {string} fileId
 * @returns {Promise<Set<string>>}
 */
export async function getPlayedChapters(fileId) {
  const states = await activeProvider.getPlayStates(fileId);
  const result = new Set();
  for (const id of Object.keys(states)) {
    if (states[id] === -1) result.add(id);
  }
  return result;
}

/**
 * Mark a chapter as fully played. Derived helper over `setPlayState(-1)`.
 * @param {string} fileId
 * @param {string} chapterId
 * @returns {Promise<void>}
 */
export function markChapterPlayed(fileId, chapterId) {
  return activeProvider.setPlayState(fileId, chapterId, -1);
}

/**
 * Clear the playback state for a chapter. Derived helper over `setPlayState(0)`.
 * @param {string} fileId
 * @param {string} chapterId
 * @returns {Promise<void>}
 */
export function markChapterUnplayed(fileId, chapterId) {
  return activeProvider.setPlayState(fileId, chapterId, 0);
}
